import "./GroupCardShared.scss";

import clsx from "clsx";
import type { FC, Ref } from "react";
import { useEffect, useState } from "react";
import { useBoolean } from "usehooks-ts";

import { PreviewUserNames } from "components/materials/page/groups/PreviewUserNames";
import { userActivityIcons } from "components/materials/section/helpers";
import { Trans, t } from "i18n/i18n";
import { Banner } from "mds/components/Banner";
import { Chip } from "mds/components/Chip";
import { CloseButton } from "mds/components/CloseButton";
import { IconText } from "mds/components/IconText";
import { RadioButtonCard } from "mds/components/RadioButtonCard";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { AddPlusIcon, LoadingDotsIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  pageGroupSelectors,
  pageSelectors,
  sectionUserSelectors,
  selectActivityStatusForUser,
  selectCurrentSectionId,
  selectCurrentSectionUserId,
  selectCurrentUserId,
  selectEmptyPageGroupCountByPageId,
  selectPreviewUserIdsForPageGroup,
  selectSortedPageGroupUsersByPageGroupId,
  selectUserBySectionUserId,
} from "store/selectors";
import { useFullListSelector, useListSelector, useObjectSelector } from "store/store-hooks";
import { toastLocalizedOperationCatcher } from "utils/alerts";
import { compactMap } from "utils/collections";
import { usePageGroupPreview } from "utils/hooks/use-page-group-preview";
import { PROFILE_PROPERTIES, getUserFullName } from "utils/user-utils";

interface StudentSelfSelectCardProps {
  closeButtonRef?: Ref<HTMLButtonElement>;
  pageId: string;
  onClose: () => void;
}

const MAX_EMPTY_GROUPS_ALLOWED = 2;

interface SelfSelectMemberProps {
  sectionUserId: string;
}

const SelfSelectMember: FC<SelfSelectMemberProps> = ({ sectionUserId }) => {
  const user = useObjectSelector(
    (s) => selectUserBySectionUserId(s, sectionUserId),
    PROFILE_PROPERTIES,
  );
  const activityStatus = useAppSelector((s) => selectActivityStatusForUser(s, user?.id));

  return (
    <Chip className="min-w-0 max-w-full" kind="white" size="s">
      {userActivityIcons[activityStatus]}
      <span className="truncate">{getUserFullName(user)}</span>
    </Chip>
  );
};

interface StudentSelfSelectGroupRowProps {
  pageGroupId: string;
  pageGroupIndex: number;
  pageId: string;
  selectedPageGroupId: string | null;
  onSelectGroup: (pageGroupId: string) => void;
}

const StudentSelfSelectGroupRow: FC<StudentSelfSelectGroupRowProps> = ({
  pageGroupId,
  pageGroupIndex,
  pageId,
  selectedPageGroupId,
  onSelectGroup,
}) => {
  const page = useObjectSelector((s) => pageSelectors.selectById(s, pageId), ["max_group_size"]);
  const pageGroupUsers = useListSelector(
    (s) => selectSortedPageGroupUsersByPageGroupId(s, pageGroupId),
    ["id", "section_user_id"],
  );
  const memberSectionUsers = useListSelector(
    (state) =>
      compactMap(pageGroupUsers, (pageGroupUser) =>
        sectionUserSelectors.selectById(state, pageGroupUser.section_user_id),
      ),
    ["user_id"],
  );

  const previewUserIdsForGroup = useListSelector((state) =>
    selectPreviewUserIdsForPageGroup(state, pageId, pageGroupId),
  );

  const currentUserId = useAppSelector(selectCurrentUserId);
  const isSelected = selectedPageGroupId === pageGroupId;
  const maxGroupSize = page?.max_group_size ?? null;
  const isFull = maxGroupSize !== null && pageGroupUsers.length >= maxGroupSize;

  const memberUserIds = new Set(memberSectionUsers.map((sectionUser) => sectionUser.user_id));
  const otherPreviewUserIds = previewUserIdsForGroup.filter(
    (userId) => !memberUserIds.has(userId) && userId !== currentUserId,
  );
  const previewUserIds =
    currentUserId && isSelected && !memberUserIds.has(currentUserId)
      ? [...otherPreviewUserIds, currentUserId]
      : otherPreviewUserIds;

  const memberCount = pageGroupUsers.length;
  const spotsLabel = isFull
    ? t("student_self_select_card.group_is_full")
    : maxGroupSize === null
      ? t("student_self_select_card.spots_status_unlimited", { joined: memberCount })
      : t("student_self_select_card.spots_status", {
          count: Math.max(0, maxGroupSize - memberCount),
          joined: memberCount,
        });

  const hasMembers = pageGroupUsers.length > 0 || previewUserIds.length > 0;

  return (
    <RadioButtonCard
      checked={isSelected}
      className="w-full"
      disabled={isFull}
      label={t("common.group_title", { number: pageGroupIndex + 1 })}
      name={`page-group-select-${pageId}`}
      sublabel={spotsLabel}
      value={pageGroupId}
      onChange={() => onSelectGroup(pageGroupId)}
    >
      {hasMembers ? (
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {pageGroupUsers.map((pageGroupUser) => (
            <SelfSelectMember
              key={pageGroupUser.id}
              sectionUserId={pageGroupUser.section_user_id}
            />
          ))}

          <PreviewUserNames userIds={previewUserIds} />
        </div>
      ) : (
        <div className="body-xs text-black-tint-55">{t("student_self_select_card.no_members")}</div>
      )}
    </RadioButtonCard>
  );
};

export const StudentSelfSelectCard: FC<StudentSelfSelectCardProps> = ({
  closeButtonRef,
  pageId,
  onClose,
}) => {
  const page = useObjectSelector(
    (s) => pageSelectors.selectById(s, pageId),
    ["locked_at", "max_group_size"],
  );
  const sectionId = useAppSelector(selectCurrentSectionId);
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const { value: isFetching, setTrue: setFetching, setFalse: setNotFetching } = useBoolean(false);
  const { currentUserPreviewGroupId, setPreview, clearPreview } = usePageGroupPreview(pageId);
  const [localSelectedGroupId, setLocalSelectedGroupId] = useState<string | null>(null);

  // A group the student was on when it filled. Kept so an opening spot does not
  // reselect them; they have to choose again.
  const [droppedFullGroupId, setDroppedFullGroupId] = useState<string | null>(null);
  const requestedGroupId = localSelectedGroupId ?? currentUserPreviewGroupId;
  const pageGroups = useListSelector((s) => pageGroupSelectors.filterByPageId(s, pageId), ["id"]);
  const emptyPageGroupCount = useAppSelector((s) => selectEmptyPageGroupCountByPageId(s, pageId));
  const selectedGroupUsers = useFullListSelector((s) =>
    selectSortedPageGroupUsersByPageGroupId(s, requestedGroupId),
  );
  const maxGroupSize = page?.max_group_size ?? null;
  const isRequestedGroupFull = maxGroupSize !== null && selectedGroupUsers.length >= maxGroupSize;

  if (requestedGroupId && isRequestedGroupFull && droppedFullGroupId !== requestedGroupId) {
    setDroppedFullGroupId(requestedGroupId);
  }

  const selectedGroupId =
    requestedGroupId &&
    requestedGroupId !== droppedFullGroupId &&
    !isRequestedGroupFull &&
    pageGroups.some((pg) => pg.id === requestedGroupId)
      ? requestedGroupId
      : null;

  useEffect(() => {
    if (!droppedFullGroupId) {
      return;
    }

    clearPreview();
  }, [clearPreview, droppedFullGroupId]);

  // Allow up to 2 empty groups so students can add a group without waiting on
  // API lag, without leaving an unbounded number of empty groups.
  const shouldShowAddGroup = !page?.locked_at && emptyPageGroupCount < MAX_EMPTY_GROUPS_ALLOWED;

  const onSelectGroup = (pageGroupId: string) => {
    if (droppedFullGroupId === pageGroupId) {
      setDroppedFullGroupId(null);
    }
    setLocalSelectedGroupId(pageGroupId);
    setPreview(pageGroupId);
  };

  const onJoinGroup = async (pageGroupId: string) => {
    if (!sectionUserId || !sectionId) {
      return;
    }

    setFetching();

    try {
      await storeApi.page_group_users.create({
        page_group_id: pageGroupId,
        section_user_id: sectionUserId,
        section_id: sectionId,
      });

      clearPreview();
    } catch (error) {
      toastLocalizedOperationCatcher("failed_to_join_group")(error);
    } finally {
      setNotFetching();
    }
  };

  const onAddGroup = async () => {
    if (!sectionId || !sectionUserId) {
      return;
    }

    setFetching();

    try {
      const pageGroup = await storeApi.page_groups.create({
        page_id: pageId,
        section_id: sectionId,
      });

      // Membership is written only when the student confirms with Join Group.
      onSelectGroup(pageGroup.id);
    } catch (error) {
      toastLocalizedOperationCatcher("failed_to_create_new_group")(error);
    } finally {
      setNotFetching();
    }
  };

  if (!page || !sectionId) {
    return null;
  }

  return (
    <div className="group-card flex w-full flex-col gap-3 rounded-[20px] border border-black-tint-90 bg-black-tint-97 p-6 supports-squircle:rounded-[40px] supports-squircle:corner-squircle">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="body-m text-black-tint-20">{t("student_self_select_card.title")}</h3>

          <p className="body-s mt-1 text-black-tint-40">
            <Trans
              components={{
                join: <strong className="font-semibold text-black-tint-20" />,
              }}
              i18nKey="student_self_select_card.instructions"
              values={{ joinLabel: t("student_self_select_card.join_group") }}
            />
          </p>
        </div>

        <CloseButton ref={closeButtonRef} size="s" onClick={onClose} />
      </div>

      {page.locked_at ? (
        <Banner kind="info">{t("lock_status.group_card_lock_warning")}</Banner>
      ) : (
        <div
          aria-label={t("glossary.page_group")}
          className="flex flex-col gap-3"
          role="radiogroup"
        >
          {pageGroups.map((pageGroup, index) => (
            <StudentSelfSelectGroupRow
              key={pageGroup.id}
              pageGroupId={pageGroup.id}
              pageGroupIndex={index}
              pageId={pageId}
              selectedPageGroupId={selectedGroupId}
              onSelectGroup={onSelectGroup}
            />
          ))}
        </div>
      )}

      {(shouldShowAddGroup || (selectedGroupId && !page.locked_at)) && (
        <div className="flex items-center justify-between">
          {shouldShowAddGroup && (
            <SquareButton kind="ghost" size="xs" onClick={onAddGroup}>
              <IconText iconStart={<AddPlusIcon />} text={t("common.add.page_group")} />
            </SquareButton>
          )}

          {selectedGroupId && !page.locked_at && (
            <SquareButton
              className={clsx("ml-auto", isFetching && "cursor-not-allowed")}
              kind="accent"
              size="xs"
              onClick={isFetching ? undefined : () => onJoinGroup(selectedGroupId)}
            >
              {isFetching ? (
                <LoadingDotsIcon aria-label={t("common.loading")} />
              ) : (
                t("student_self_select_card.join_group")
              )}
            </SquareButton>
          )}
        </div>
      )}
    </div>
  );
};
